import { useState } from "react";
import type { Authorization, Product, SoldOutProduct } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { productsIn, sellableCategories } from "../sale/menu-view";

interface Props {
  /** The code that opened Beheer; every change is made in this person's name. */
  authorization: Authorization;
}

/**
 * Beheer › Uitverkocht, as in the prototype: what is sold out now on top, a search field, and every
 * category to open. Tapping a product sets it sold out (red) or available again; every device of the
 * shop follows at once.
 */
export function SoldOutScreen({ authorization }: Props) {
  const { api, menu, soldOut, soldOutChanged, report } = useTill();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const data = menu.menu;
  if (data === null) return null;

  const categories = sellableCategories(data);
  const everything = categories.flatMap((category) => productsIn(data, category.id));
  const outNow = everything.filter((product) => soldOut.has(product.id));
  const search = query.trim().toLowerCase();

  const toggle = async (product: Product) => {
    if (busy !== null) return;
    setBusy(product.id);
    try {
      const { products } = await api.request<{ products: SoldOutProduct[] }>(
        "PUT",
        `/device/sold-out/${encodeURIComponent(product.id)}`,
        { staffToken: authorization.token, body: { soldOut: !soldOut.has(product.id) } },
      );
      soldOutChanged(products);
    } catch (error) {
      report(error);
    } finally {
      setBusy(null);
    }
  };

  const chip = (product: Product) => (
    <button
      key={product.id}
      type="button"
      className="chip minus"
      aria-pressed={soldOut.has(product.id)}
      disabled={busy === product.id}
      onClick={() => void toggle(product)}
    >
      {product.name}
      <small>{soldOut.has(product.id) ? t.soldOut.chip : ""}</small>
    </button>
  );

  return (
    <div className="panel admin-section">
      <h2>{t.soldOut.title}</h2>
      <p className="note">{t.soldOut.note}</p>
      <div className="grp">
        <h3>
          {t.soldOut.now} <em>{outNow.length}</em>
        </h3>
        {outNow.length > 0 ? (
          <div className="chips">{outNow.map(chip)}</div>
        ) : (
          <span className="note">{t.soldOut.nothing}</span>
        )}
      </div>
      <input
        className="so-search"
        placeholder={t.soldOut.search}
        aria-label={t.soldOut.search}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
        }}
      />
      <div className="solist">
        {categories.map((category) => {
          const all = productsIn(data, category.id);
          const shown = search === "" ? all : all.filter((product) => product.name.toLowerCase().includes(search));
          if (search !== "" && shown.length === 0) return null;
          const expanded = search !== "" || open.has(category.id);
          const out = all.filter((product) => soldOut.has(product.id)).length;
          return (
            <div key={category.id} className="socat">
              <button
                type="button"
                className="sohead"
                aria-expanded={expanded}
                onClick={() => {
                  setOpen((current) => {
                    const next = new Set(current);
                    if (next.has(category.id)) next.delete(category.id);
                    else next.add(category.id);
                    return next;
                  });
                }}
              >
                <span>
                  {expanded ? "▾" : "▸"} {category.name}
                </span>
                <span className="note">
                  {t.soldOut.products(all.length)}
                  {out > 0 && (
                    <>
                      {" · "}
                      <b className="sored">{t.soldOut.soldOutCount(out)}</b>
                    </>
                  )}
                </span>
              </button>
              {expanded && <div className="chips sobody">{shown.map(chip)}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
